import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Avatar,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  Container,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Phone as PhoneIcon, Person as PersonIcon, Email as EmailIcon, Work as WorkIcon, Logout as LogoutIcon, ColorLens as ColorLensIcon } from '@mui/icons-material';
import { userAPI } from '../services/api';
import AuthService from '../services/auth';
import { ThemeColorContext } from '../App';
import { SketchPicker } from 'react-color';

const Profile = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [phone, setPhone] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);

  // Get theme color context
  const { themeColor, setThemeColor, isColorPickerEnabled } = useContext(ThemeColorContext);

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        const response = await userAPI.getCurrentUser();
        setUser(response.data);
        setPhone(response.data.phone || '');
        setLoading(false);
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError('Не вдалося завантажити дані користувача');
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  const handleEditClick = () => {
    setIsEditing(true);
  };

  const handleCancelClick = () => {
    setIsEditing(false);
    setPhone(user.phone || '');
  };

  const handleSaveClick = async () => {
    try {
      const updatedUser = { ...user, phone };
      await userAPI.updateCurrentUser(updatedUser);

      // No need to update localStorage as user data is stored on the server

      setUser(updatedUser);
      setIsEditing(false);
      setSaveSuccess(true);
    } catch (err) {
      console.error('Error updating user data:', err);
      setSaveError(true);
    }
  };

  const handleCloseSnackbar = () => {
    setSaveSuccess(false);
    setSaveError(false);
  };

  const handleLogout = () => {
    AuthService.logout();
  };

  // Color picker handlers
  const handleOpenColorPicker = () => {
    setColorPickerOpen(true);
  };

  const handleCloseColorPicker = () => {
    setColorPickerOpen(false);
  };

  const handleColorChange = (color) => {
    setThemeColor(color.hex);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Container maxWidth="md">
      <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 4, fontWeight: 'bold' }}>
        Профіль користувача
      </Typography>

      <Paper elevation={3} sx={{ p: 4, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <Avatar 
            sx={{ 
              width: 80, 
              height: 80, 
              bgcolor: 'primary.main',
              fontSize: '2rem',
              fontWeight: 'bold',
              mr: 3
            }}
          >
            {user?.firstName?.charAt(0) || user?.email?.charAt(0).toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              {user?.firstName} {user?.lastName}
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {user?.role === 'ADMIN' ? 'Адміністратор' : 'Користувач'}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PersonIcon sx={{ mr: 2, color: 'primary.main' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                Повне ім'я
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ ml: 5, mb: 3 }}>
              {user?.lastName} {user?.firstName} {user?.middleName || ''}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <EmailIcon sx={{ mr: 2, color: 'primary.main' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                Email
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ ml: 5, mb: 3, wordBreak: 'break-word', overflowWrap: 'break-word' }}>
              {user?.email}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <WorkIcon sx={{ mr: 2, color: 'primary.main' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                Посада
              </Typography>
            </Box>
            <Typography variant="body1" sx={{ ml: 5, mb: 3 }}>
              {user?.position || 'Не вказано'}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <PhoneIcon sx={{ mr: 2, color: 'primary.main' }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                Номер телефону
              </Typography>
            </Box>

            {isEditing ? (
              <Box sx={{ ml: 5, mb: 3 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Введіть номер телефону"
                  size="small"
                  sx={{ mb: 2 }}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button 
                    variant="contained" 
                    color="primary" 
                    onClick={handleSaveClick}
                    sx={{ borderRadius: 2 }}
                  >
                    Зберегти
                  </Button>
                  <Button 
                    variant="outlined" 
                    onClick={handleCancelClick}
                    sx={{ borderRadius: 2 }}
                  >
                    Скасувати
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box sx={{ ml: 5, mb: 3, display: 'flex', alignItems: 'center' }}>
                <Typography variant="body1" sx={{ mr: 2 }}>
                  {user?.phone || 'Не вказано'}
                </Typography>
                <Button 
                  variant="outlined" 
                  size="small" 
                  onClick={handleEditClick}
                  sx={{ 
                    borderRadius: 2,
                    '&:hover': {
                      bgcolor: 'rgba(218, 6, 6, 0.08)',
                      transform: 'translateY(-2px)',
                      transition: 'all 0.3s ease'
                    }
                  }}
                >
                  Редагувати
                </Button>
              </Box>
            )}
          </Grid>
        </Grid>
      </Paper>

      {/* Color picker button - only shown for specific user */}
      {isColorPickerEnabled && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 4 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenColorPicker}
            startIcon={<ColorLensIcon />}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1,
              '&:hover': {
                transform: 'translateY(-2px)',
                transition: 'all 0.3s ease'
              }
            }}
          >
            Змінити колір теми
          </Button>
        </Box>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleLogout}
          startIcon={<LogoutIcon />}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1,
            '&:hover': {
              bgcolor: 'rgba(0, 0, 0, 0.04)',
              transform: 'translateY(-2px)',
              transition: 'all 0.3s ease'
            }
          }}
        >
          Вийти з системи
        </Button>
      </Box>

      <Snackbar open={saveSuccess} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
          Номер телефону успішно оновлено!
        </Alert>
      </Snackbar>

      <Snackbar open={saveError} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>
          Помилка при оновленні номера телефону
        </Alert>
      </Snackbar>

      {/* Color Picker Dialog */}
      <Dialog 
        open={colorPickerOpen} 
        onClose={handleCloseColorPicker}
        PaperProps={{
          sx: { 
            borderRadius: 2,
            maxWidth: 'auto'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 'bold', pb: 1 }}>
          Виберіть колір теми
        </DialogTitle>
        <DialogContent>
          <Box sx={{ p: 2 }}>
            <SketchPicker 
              color={themeColor}
              onChange={handleColorChange}
              disableAlpha={true}
              width="300px"
              presetColors={[
                '#da0606', // Default red
                '#3f51b5', // Indigo
                '#2196f3', // Blue
                '#009688', // Teal
                '#4caf50', // Green
                '#ff9800', // Orange
                '#795548', // Brown
                '#607d8b'  // Blue Grey
              ]}
              styles={{
                default: {
                  picker: {
                    width: '300px',
                    boxShadow: 'none',
                    borderRadius: '8px'
                  }
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleCloseColorPicker} 
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1
            }}
          >
            Застосувати
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Profile;
